import {
	DEFAULT_CURRENCY_LIST_FILTERS,
	DEFAULT_NOTIFICATIONS_FILTERS,
} from '@helpers/consts.helper';
import { Inject, Injectable } from '@angular/core';
import { Storage } from '@enums/storage.enum';
import { LOCAL_STORAGE, StorageService } from 'ngx-webstorage-service';
import { Option } from 'rustic';
import { CookieService } from 'ngx-cookie-service';
import { ChangeLevel } from '@enums/notifications';

@Injectable({
	providedIn: 'root',
})
export class LocalStorageManagerService {
	constructor(
		@Inject(LOCAL_STORAGE) private storage: StorageService,
		private _cs: CookieService,
	) {}

	remove(item: string): void {
		this.storage.remove(item);
	}

	get currentLanguage(): string | null {
		// Polish-first product — default to PL unless the user explicitly picked another language.
		return this.storage.get(Storage.CurrentLanguage) || 'pl';
	}

	set currentLanguage(language: string) {
		this.storage.set(Storage.CurrentLanguage, language);
	}

	get refUser(): string | null {
		return this.storage.get(Storage.ReferenceUser) || null;
	}

	set refUser(ref: string) {
		this.storage.set(Storage.ReferenceUser, ref);
	}

	get intervalCurrencyList(): number | null {
		return this.storage.get(Storage.IntervalCurrencyList) ?? 1440;
	}

	set intervalCurrencyList(interval: number) {
		this.storage.set(Storage.IntervalCurrencyList, interval);
	}

	get filtersCurrencyList(): { [key: string]: Option<string> } | null {
		return this.storage.get(Storage.FiltersCurrencyList) ?? DEFAULT_CURRENCY_LIST_FILTERS;
	}

	set filtersCurrencyList(filters: { [key: string]: Option<string> }) {
		this.storage.set(Storage.FiltersCurrencyList, filters);
	}

	get sortingCurrencyList(): { name: string; type: string } | null {
		return this.storage.get(Storage.SortingCurrencyList) ?? null;
	}

	set sortingCurrencyList(sorting: { name: string; type: string }) {
		this.storage.set(Storage.SortingCurrencyList, sorting);
	}

	get filtersNotifications(): { [key: string]: Option<string> | string[] | ChangeLevel } | null {
		const filters =
			this.storage.get(Storage.FiltersNotifications) ?? DEFAULT_NOTIFICATIONS_FILTERS;

		if (filters.level === null) {
			filters.level = [];
		}

		if (filters.endName === null) {
			filters.endName = [];
		}

		return filters;
	}

	set filtersNotifications(filters: { [key: string]: Option<string | string[] | ChangeLevel> }) {
		this.storage.set(Storage.FiltersNotifications, filters);
	}

	get intervalNotifications(): number | null {
		return this.storage.get(Storage.IntervalNotifications) ?? 5;
	}

	set intervalNotifications(interval: number) {
		this.storage.set(Storage.IntervalNotifications, interval);
	}

	getTemplate(isDesktopView: boolean, view: string): string {
		const defaultTemplate = isDesktopView ? 'standard' : 'standard';
		return (
			this.storage.get(`template-${view}-${isDesktopView ? 'desktop' : 'mobile'}`) ??
			defaultTemplate
		);
	}

	setTemplate(isDesktopView: boolean, view: string, template: string): void {
		this.storage.set(`template-${view}-${isDesktopView ? 'desktop' : 'mobile'}`, template);
	}

	// Cookies
	get authToken(): string {
		return this._cs.get('X-Auth') ?? '';
	}

	deleteAuthToken(): void {
		this._cs.delete('X-Auth');
	}
}
